import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import useQueryLibraryCore from './useQueryLibraryCore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';


// Constants
const PINNED_QUERIES_KEY = 'pinned_queries';
const MAX_PINNED_QUERIES = 5;

/**
 * Hook for query library UI operations built on top of the core hook
 * @returns {Object} Enhanced hook with UI-specific functionality
 */
export const useQueryLibraryUI = () => {
  const { currentUser } = useAuth();
  const { config } = useConfig();
  
  // Use the core hook for data fetching and caching
  const coreLibrary = useQueryLibraryCore();
  
  // Initialize pinned queries from localStorage
  const [pinnedQueries, setPinnedQueries] = useState(() => {
    try {
      const pinnedData = localStorage.getItem(PINNED_QUERIES_KEY);
      if (pinnedData) {
        const { queryIds, userId } = JSON.parse(pinnedData);
        if (userId === currentUser?.email) {
          return queryIds || [];
        }
      }
    } catch (error) {
    }
    return [];
  });

  // Function to save pinned queries to localStorage
  const savePinnedQueries = useCallback((queryIds) => {
    try {
      localStorage.setItem(PINNED_QUERIES_KEY, JSON.stringify({
        queryIds,
        userId: currentUser?.email
      }));
    } catch (error) {
    }
  }, [currentUser]);

  // Function to toggle pin status
  const togglePinQuery = useCallback((queryId) => {
    setPinnedQueries(prev => {
      let newPinnedQueries;
      
      if (prev.includes(queryId)) {
        // Unpin: remove from pinned queries
        newPinnedQueries = prev.filter(id => id !== queryId);
      } else {
        // Pin: add to pinned queries, respecting the maximum limit
        if (prev.length >= MAX_PINNED_QUERIES) {
          // If at max limit, remove the oldest pin
          newPinnedQueries = [...prev.slice(1), queryId];
        } else {
          newPinnedQueries = [...prev, queryId];
        }
      }
      
      savePinnedQueries(newPinnedQueries);
      return newPinnedQueries;
    });
  }, [savePinnedQueries]);

  // Pass through hideQuery function from core
  const hideQuery = useCallback(async (queryId) => {
    // Clean up pins if needed
    if (pinnedQueries.includes(queryId)) {
      const newPinnedQueries = pinnedQueries.filter(id => id !== queryId);
      setPinnedQueries(newPinnedQueries);
      savePinnedQueries(newPinnedQueries);
    }
    // Call the core hideQuery function
    return await coreLibrary.hideQuery(queryId);
  }, [coreLibrary.hideQuery, pinnedQueries, setPinnedQueries, savePinnedQueries]);

  // Filter queries with explanation object
const filteredQueries = useCallback((searchQuery = '') => {
  // Show all queries instead of filtering by explanation
  const allQueries = coreLibrary.queries;
  
  // Then apply search filtering if there's a search query
  if (searchQuery.trim() === '') {
    return allQueries;
  }
  
  const searchLower = searchQuery.toLowerCase();
  return allQueries.filter(query => 
    // Search in explanation title if it exists
    (query.explanation && 
     typeof query.explanation === 'object' && 
     query.explanation.title && 
     query.explanation.title.toLowerCase().includes(searchLower)) ||
    // Search in query name
    (query.name && 
     query.name.toLowerCase().includes(searchLower)) || 
    // Search in original user query
    (query.userQuery && 
     query.userQuery.toLowerCase().includes(searchLower))
  );
}, [coreLibrary.queries]);

  // Get sorted queries with pinned ones on top
  const getSortedQueries = useCallback((filteredQueriesList) => {
    if (!filteredQueriesList || !pinnedQueries.length) return filteredQueriesList;

    // Separate pinned and unpinned queries
    const pinned = [];
    const unpinned = [];
    
    filteredQueriesList.forEach(query => {
      if (pinnedQueries.includes(query.id)) {
        pinned.push(query);
      } else {
        unpinned.push(query);
      }
    });
    
    // Sort pinned queries according to the order in pinnedQueries array
    pinned.sort((a, b) => {
      return pinnedQueries.indexOf(a.id) - pinnedQueries.indexOf(b.id);
    });
    
    // Return pinned queries followed by unpinned queries
    return [...pinned, ...unpinned];
  }, [pinnedQueries]);

// Enhanced runSavedQuery function to apply stored visualization settings
const runSavedQuery = useCallback(async (query, onQueryResult, onQueryStart, setIsLoading, setError) => {
  if (!query || !currentUser?.email || !config?.core?.projectId) return;
    
  onQueryStart?.();
  setIsLoading?.(true);
  
  try {
    const queryDocRef = doc(db, 'SavedQueries', query.id);
    const queryDoc = await getDoc(queryDocRef);
    
    let upToDateQuery = query;
    
    if (queryDoc.exists()) {
      const queryData = queryDoc.data();
      upToDateQuery = {
        ...query,
        userSettings: queryData.userSettings || {},
        cardSettings: {
          ...queryData.cardSettings,
          transformationType: queryData.cardSettings?.transformationType,
          pivotConfig:        queryData.cardSettings?.pivotConfig
        },
        originalSchema: queryData.originalSchema,
        originalResult: queryData.originalResult,
        originalLlmQueryId: queryData.originalLlmQueryId  // ADD THIS LINE
      };
    }
    
    const response = await fetch(
      `https://us-central1-${config.core.projectId}.cloudfunctions.net/runSavedQuery`, 
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          data: { savedQueryId: query.id, userId: currentUser.email }, 
          context: { auth: true } 
        })
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to run query');
    }
    
    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }
    if (!data.result || !Array.isArray(data.result)) {
      throw new Error('Invalid query result structure');
    }
    
    const userSettings = upToDateQuery.userSettings[currentUser.email] || {};
    
    let combinedCardSettings = {
      viewMode:  'table',
      chartType: 'bar',
      ...(data.cardSettings      || {}),
      ...(upToDateQuery.cardSettings || {}),
      ...userSettings
    };
    
    if (combinedCardSettings.transformationType) {
      delete combinedCardSettings.visibleColumns;
      delete combinedCardSettings.columnOrder;
    }
    
    const processedResult = {
      ...data,
      userQuery:      upToDateQuery.userQuery || '',
      name:           upToDateQuery.name || 'Saved Query',
      isSavedQuery:   true,
      documentId:     data.documentId || upToDateQuery.originalLlmQueryId || null,
      config: {
        usedModel:    data.usedModel || upToDateQuery.llmModel || 'Unknown',
        fallbackUsed: Boolean(data.fallbackUsed || upToDateQuery.fallbackUsed),
      },
      cardSettings:   combinedCardSettings,
      userSettings:   upToDateQuery.userSettings,
      originalSchema: upToDateQuery.originalSchema,
      originalResult: upToDateQuery.originalResult,
      processedData:  Array.isArray(data.processedData) && data.processedData.length > 0
                        ? data.processedData
                        : [...data.result]
    };
    
    onQueryResult?.(processedResult);
    return processedResult;
  } catch (error) {
    setError?.({ message: error.message || 'Error running query', status: 'error' });
    onQueryResult?.(null);
    throw error;
  } finally {
    setIsLoading?.(false);
  }
}, [config, currentUser, db]);

const saveQueryTitle = useCallback(async (queryId, title) => {
  if (!queryId || !title || !currentUser?.email) {
    return;
  }
  
  try {
    // Save the title as part of user settings
    await coreLibrary.saveQuerySettings(queryId, { 
      customTitle: title.trim() 
    });
    
    return true;
  } catch (error) {
    return false;
  }
}, [coreLibrary, currentUser]);


  return {
    // Re-export core state and functions
    ...coreLibrary,
    
    // UI-specific state
    pinnedQueries,
    
    // UI-specific functions
    filteredQueries,
    runSavedQuery,
    togglePinQuery,
    getSortedQueries,
    hideQuery, // Re-export the enhanced hideQuery function
    saveQueryTitle, // Add the new function for saving titles
    
    // Constants
    MAX_PINNED_QUERIES,
  };
};

export default useQueryLibraryUI;